<script setup>
import { reactive, ref, watch, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import {
  CSAT_DISPLAY_TYPES,
  CSAT_SUBMISSION_WINDOW_UNITS,
} from 'shared/constants/messages';

import WithLabel from 'v3/components/Form/WithLabel.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import CSATDisplayTypeSelector from 'dashboard/routes/dashboard/settings/inbox/settingsPage/components/CSATDisplayTypeSelector.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages.js';

const props = defineProps({
  csatSurveyEnabled: { type: Boolean, default: false },
  csatConfig: {
    type: Object,
    default: () => ({
      display_type: 'emoji',
      message: '',
      button_text: 'Please rate us',
      language: 'en',
      survey_rules: { operator: 'contains', values: [] },
      submission_window_value: 14,
      submission_window_unit: CSAT_SUBMISSION_WINDOW_UNITS.DAYS,
    }),
  },
});

const emit = defineEmits(['update:csatSurveyEnabled', 'update:csatConfig']);

const { t } = useI18n();
const labels = useMapGetter('labels/getLabels');

const selectedLabelValues = ref([]);
const currentLabel = ref('');

const state = reactive({
  displayType: CSAT_DISPLAY_TYPES.EMOJI,
  message: '',
  buttonText: 'Please rate us',
  surveyRuleOperator: 'contains',
  language: 'en',
  submissionWindowValue: 14,
  submissionWindowUnit: CSAT_SUBMISSION_WINDOW_UNITS.DAYS,
});

const filterTypes = [
  {
    label: t('INBOX_MGMT.CSAT.SURVEY_RULE.OPERATOR.CONTAINS'),
    value: 'contains',
  },
  {
    label: t('INBOX_MGMT.CSAT.SURVEY_RULE.OPERATOR.DOES_NOT_CONTAINS'),
    value: 'does_not_contain',
  },
];

const labelOptions = computed(() =>
  labels.value?.length
    ? labels.value
        .map(label => ({ label: label.title, value: label.title }))
        .filter(label => !selectedLabelValues.value.includes(label.value))
    : []
);

const languageOptions = computed(() =>
  languages.map(({ name, id }) => ({ label: `${name} (${id})`, value: id }))
);

const submissionWindowUnitOptions = computed(() => [
  {
    label: t('INBOX_MGMT.CSAT.SUBMISSION_WINDOW.UNITS.MINUTES'),
    value: CSAT_SUBMISSION_WINDOW_UNITS.MINUTES,
  },
  {
    label: t('INBOX_MGMT.CSAT.SUBMISSION_WINDOW.UNITS.HOURS'),
    value: CSAT_SUBMISSION_WINDOW_UNITS.HOURS,
  },
  {
    label: t('INBOX_MGMT.CSAT.SUBMISSION_WINDOW.UNITS.DAYS'),
    value: CSAT_SUBMISSION_WINDOW_UNITS.DAYS,
  },
]);

const resolveSubmissionWindow = cfg => {
  if (
    cfg.submission_window_value != null ||
    cfg.submission_window_unit != null
  ) {
    return {
      value: cfg.submission_window_value ?? 14,
      unit: cfg.submission_window_unit || CSAT_SUBMISSION_WINDOW_UNITS.DAYS,
    };
  }

  if (cfg.submission_window_days != null) {
    return {
      value: cfg.submission_window_days,
      unit: CSAT_SUBMISSION_WINDOW_UNITS.DAYS,
    };
  }

  return {
    value: 14,
    unit: CSAT_SUBMISSION_WINDOW_UNITS.DAYS,
  };
};

let isSyncingFromProps = false;

const areLabelValuesEqual = (nextValues = [], currentValues = []) => {
  if (nextValues.length !== currentValues.length) return false;
  return nextValues.every((value, index) => value === currentValues[index]);
};

// Sync incoming props into local state without re-emitting to the parent.
const syncFromProps = () => {
  const cfg = props.csatConfig || {};
  const nextLabelValues = Array.isArray(cfg.survey_rules?.values)
    ? [...cfg.survey_rules.values]
    : [];

  isSyncingFromProps = true;
  state.displayType = cfg.display_type || CSAT_DISPLAY_TYPES.EMOJI;
  state.message = cfg.message || '';
  state.buttonText = cfg.button_text || 'Please rate us';
  state.language = cfg.language || 'en';
  state.surveyRuleOperator = cfg.survey_rules?.operator || 'contains';
  const { value, unit } = resolveSubmissionWindow(cfg);
  state.submissionWindowValue = value;
  state.submissionWindowUnit = unit;

  if (!areLabelValuesEqual(nextLabelValues, selectedLabelValues.value)) {
    selectedLabelValues.value = nextLabelValues;
  }

  nextTick(() => {
    isSyncingFromProps = false;
  });
};

watch(() => props.csatConfig, syncFromProps, { immediate: true, deep: true });

const emitConfig = () => {
  if (isSyncingFromProps) return;

  emit('update:csatConfig', {
    display_type: state.displayType,
    message: state.message,
    button_text: state.buttonText,
    language: state.language,
    survey_rules: {
      operator: state.surveyRuleOperator,
      values: [...selectedLabelValues.value],
    },
    submission_window_value: state.submissionWindowValue,
    submission_window_unit: state.submissionWindowUnit,
  });
};

watch(state, emitConfig, { deep: true });
watch(selectedLabelValues, emitConfig, { deep: true });

const handleLabelSelect = value => {
  if (!value || selectedLabelValues.value.includes(value)) return;
  selectedLabelValues.value.push(value);
  currentLabel.value = '';
};

const removeLabel = label => {
  const index = selectedLabelValues.value.indexOf(label);
  if (index !== -1) selectedLabelValues.value.splice(index, 1);
};

const updateDisplayType = type => {
  state.displayType = type;
};

const updateSurveyRuleOperator = operator => {
  state.surveyRuleOperator = operator;
};
</script>

<template>
  <div>
    <SettingsToggleSection
      :model-value="csatSurveyEnabled"
      :header="$t('INBOX_MGMT.CSAT.TITLE')"
      :description="$t('INBOX_MGMT.CSAT.SUBTITLE')"
      @update:model-value="$emit('update:csatSurveyEnabled', $event)"
    >
      <template v-if="csatSurveyEnabled" #editor>
        <div class="grid gap-5">
          <WithLabel
            :label="$t('INBOX_MGMT.CSAT.DISPLAY_TYPE.LABEL')"
            name="display_type"
          >
            <CSATDisplayTypeSelector
              :selected-type="state.displayType"
              @update="updateDisplayType"
            />
          </WithLabel>

          <WithLabel
            :label="$t('INBOX_MGMT.CSAT.MESSAGE.LABEL')"
            name="message"
          >
            <Editor
              v-model="state.message"
              :placeholder="$t('INBOX_MGMT.CSAT.MESSAGE.PLACEHOLDER')"
              :max-length="200"
              class="w-full"
            />
          </WithLabel>

          <WithLabel
            :label="$t('INBOX_MGMT.CSAT.LANGUAGE.LABEL')"
            name="language"
          >
            <ComboBox
              v-model="state.language"
              :options="languageOptions"
              :placeholder="$t('INBOX_MGMT.CSAT.LANGUAGE.PLACEHOLDER')"
            />
          </WithLabel>

          <WithLabel
            :label="$t('INBOX_MGMT.CSAT.SUBMISSION_WINDOW.LABEL')"
            name="submission_window"
          >
            <div class="flex gap-3 items-start max-w-md">
              <Input
                v-model="state.submissionWindowValue"
                type="number"
                min="0"
                :placeholder="
                  $t('INBOX_MGMT.CSAT.SUBMISSION_WINDOW.PLACEHOLDER')
                "
                class="w-full"
              />
              <ComboBox
                v-model="state.submissionWindowUnit"
                :options="submissionWindowUnitOptions"
                class="w-full min-w-[8rem]"
              />
            </div>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.CSAT.SUBMISSION_WINDOW.HELP') }}
            </p>
          </WithLabel>

          <WithLabel
            :label="$t('INBOX_MGMT.CSAT.SURVEY_RULE.LABEL')"
            name="survey_rule"
          >
            <div class="mb-4">
              <span
                class="inline-flex flex-wrap gap-1.5 items-center text-sm text-n-slate-12"
              >
                {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.DESCRIPTION_PREFIX') }}
                <FilterSelect
                  v-model="state.surveyRuleOperator"
                  variant="faded"
                  :options="filterTypes"
                  class="inline-flex shrink-0"
                  @update:model-value="updateSurveyRuleOperator"
                />
                {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.DESCRIPTION_SUFFIX') }}
                <NextButton
                  v-for="label in selectedLabelValues"
                  :key="label"
                  sm
                  faded
                  slate
                  trailing-icon
                  :label="label"
                  icon="i-lucide-x"
                  class="inline-flex shrink-0"
                  @click="removeLabel(label)"
                />
                <FilterSelect
                  v-model="currentLabel"
                  :options="labelOptions"
                  :label="$t('INBOX_MGMT.CSAT.SURVEY_RULE.SELECT_PLACEHOLDER')"
                  hide-label
                  variant="faded"
                  class="inline-flex shrink-0"
                  @update:model-value="handleLabelSelect"
                />
              </span>
            </div>
          </WithLabel>

          <p class="text-sm italic text-n-slate-11">
            {{ $t('INBOX_MGMT.CSAT.NOTE') }}
          </p>
        </div>
      </template>
    </SettingsToggleSection>
  </div>
</template>
